/**
 * Twilio module types, constants, and schemas.
 * Ported from twilio-things.js (UA_TPA_FEATURES)
 */

// ── Twilio API Response Types ────────────────────────────────────────

export interface TwilioAccount {
  sid: string;
  friendly_name: string;
  status: string;
  type?: string;
  auth_token?: string;
}

export interface TwilioPhoneNumber {
  sid: string;
  phone_number: string;
  friendly_name: string;
  capabilities: {
    sms: boolean;
    mms: boolean;
    voice: boolean;
    fax: boolean;
    whatsapp?: boolean;
  };
  voice_application_sid: string | null;
}

export interface TwilioWhatsAppSender {
  sid: string;
  sender_id: string; // e.g. "whatsapp:+14155238886"
  profile: {
    name: string;
    address?: string;
  };
  country_code?: string;
  /** Voice configuration — present when voice calling is set up on this sender */
  configuration?: {
    voice_application_sid?: string | null;
  };
}

// ── WhatsApp Business Calling Types ──────────────────────────────────

/** Status of an outbound WhatsApp call permission request */
export type WhatsAppCallPermissionStatus = 'none' | 'pending' | 'accepted' | 'rejected';

/** Tracks WhatsApp call permission state per contact */
export interface WhatsAppCallPermission {
  contactNumber: string;
  status: WhatsAppCallPermissionStatus;
  /** Timestamp when permission was granted (for 7-day expiry tracking) */
  grantedAt?: number;
  /** Number of calls made since permission was granted (max 5 per 24h) */
  callsMade?: number;
}

export interface TwilioMessage {
  sid: string;
  body: string;
  from: string;
  to: string;
  status: string;
  direction: 'inbound' | 'outbound-api' | 'outbound-reply' | 'outbound-call';
  date_created: string;
  date_sent: string;
  date_updated: string;
  /** ISO-8601 datetime when a scheduled message will be sent */
  send_at?: string;
  num_media: string;
  num_segments: string;
  error_code: number | null;
  error_message: string | null;
  subresource_uris?: { media: string };
  caption?: string;
  isUnRead?: boolean;
}

export interface TwilioContentTemplate {
  sid: string;
  friendly_name: string;
  language: string;
  types: Record<string, TwilioContentType>;
  date_created: string;
  date_updated: string;
  /** Present only on the ContentAndApprovals resource. A content template is
   *  just a draft until WhatsApp approves it; sending an unapproved one
   *  outside the 24h service window is delivered as a freeform message and
   *  rejected with error 63016. Absent means never submitted for approval. */
  approval_requests?: TwilioContentApproval;
}

export interface TwilioContentApproval {
  name?: string;
  category?: string;
  content_type?: string;
  /** 'approved' | 'received' (in review) | 'pending' | 'rejected' | 'paused'
   *  | 'unsubmitted' — WhatsApp defines the set, so treat anything that is
   *  not exactly 'approved' as not sendable out of session. */
  status?: string;
  rejection_reason?: string;
  allow_category_change?: boolean;
}

export interface TwilioContentType {
  body?: string;
  actions?: Array<{
    title: string;
    type?: string;
    url?: string;
    phone?: string;
    copy_code_text?: string;
  }>;
  media?: string | string[];
}

export interface TwilioMessagingService {
  sid: string;
  friendly_name: string;
  inbound_request_url: string;
  status_callback?: string;
  date_created?: string;
}

export interface TwilioServicePhoneNumber {
  sid: string;
  phone_number: string;
  country_code: string;
  capabilities: string[];
}

export interface TwilioChannelSender {
  sid: string;
  sender: string;
  country_code?: string;
}

/**
 * Unified sender info for incoming sync settings.
 * Combines SMS phone numbers and WhatsApp senders into a single structure.
 */
export interface IncomingSender {
  id: string;
  phoneNumber: string;
  friendlyName: string;
  channel: 'SMS' | 'WHATSAPP';
  isLinked: boolean;
  configureUrl: string;
}

// ── Template Processing Types ────────────────────────────────────────

export interface TemplateButton {
  text: string;
  type?: string;
  url?: string;
  phone?: string;
}

export interface ProcessedTemplate {
  id: string;
  name: string;
  body: string;
  language: string;
  variables: string[];
  buttons: TemplateButton[];
  headerMediaUrl?: string;
  headerMediaUrlWithVar?: string;
  footerText: string;
  isContentTemplate: boolean;
  contentSid: string;
  attachment?: { type: string };
}

// ── Available Content Types ──────────────────────────────────────────

export const AVAILABLE_CONTENT_TYPES = [
  'twilio/text',
  'twilio/quick-reply',
  'twilio/media',
  'twilio/location',
  'twilio/call-to-action',
  'twilio/list-picker',
  'twilio/card',
  'whatsapp/card',
  'whatsapp/authentication',
] as const;

// ── Workflow Code Templates ──────────────────────────────────────────

export const WORKFLOW_CODES: Record<string, Record<string, unknown>> = {
  SMS: {
    From: 'FILL_HERE',
    To: 'FILL_HERE',
    Body: 'FILL_HERE_Message_Text',
    module: 'FILL_HERE',
    recordId: 'FILL_HERE',
    channel: 'SMS',
    default_country_code: 'FILL_HERE',
    source: { itsMsgSource: true, type: 'uaapp-workflow' },
    ulgebra_webhook_authtoken: null,
  },
  MMS: {
    From: 'FILL_HERE',
    To: 'FILL_HERE',
    Body: 'FILL_HERE_Message_Text',
    MediaUrl: 'FILL_HERE',
    module: 'FILL_HERE',
    recordId: 'FILL_HERE',
    channel: 'MMS',
    default_country_code: 'FILL_HERE',
    source: { itsMsgSource: true, type: 'uaapp-workflow' },
    ulgebra_webhook_authtoken: null,
  },
  WhatsApp: {
    From: 'FILL_HERE',
    To: 'FILL_HERE',
    Body: 'FILL_HERE_Message_Text',
    MediaUrl: 'FILL_HERE',
    module: 'FILL_HERE',
    recordId: 'FILL_HERE',
    channel: 'WhatsApp',
    default_country_code: 'FILL_HERE',
    source: { itsMsgSource: true, type: 'uaapp-workflow' },
    ulgebra_webhook_authtoken: null,
  },
};

// ── Incoming Data Schema ─────────────────────────────────────────────

export const INCOMING_DATA_SCHEMA: Record<string, { example: string }> = {
  AccountSid: { example: 'ACaf4d4f141ceaf613a1743b42ed6c602c' },
  ApiVersion: { example: '2010-04-01' },
  Body: { example: 'Hi, how are you?' },
  From: { example: '+916381504050' },
  MessageSid: { example: 'SMd85014e10671da8bce767a5eba08b289' },
  NumSegments: { example: '1' },
  ProfileName: { example: 'Vijay' },
  SmsMessageSid: { example: 'SMd85014e10671da8bce767a5eba08b289' },
  SmsSid: { example: 'SMd85014e10671da8bce767a5eba08b289' },
  SmsStatus: { example: 'received' },
  To: { example: '+14155238886' },
  WaId: { example: '916381504050' },
  ReferralNumMedia: { example: '0' },
  MediaContentType0: { example: 'image/jpeg' },
  MediaUrl0: {
    example:
      'https://api.twilio.com/2010-04-01/Accounts/ACaf4d4f141ceaf613a1743b42ed6c602c/Messages/MM879f4f89bdf3b475a5ed414bcd5b29e2/Media/ME8c3c58035abe73f7ca7746f317c2fc37',
  },
  NumMedia: { example: '0' },
  _UA_Channel: { example: 'SMS' },
};

// ── Field Mapping Schema ─────────────────────────────────────────────

export const FIELD_MAPPING_SCHEMA: Record<string, string[]> = {
  name: ['$.ProfileName', '$.profileName'],
  phone: ['$.WaId', '$.From', '$.To', '$.from', '$.to', '$.waId'],
};

// ── Client IDs per Extension ─────────────────────────────────────────

export const CLIENT_IDS: Record<string, string> = {
  twilioforhubspotcrm: '28e3fcba-92f6-4301-8a82-7381f8b658a9',
  twilioforbitrix24: 'app.618df869835df8.59780487',
  twilioforpipedrive: 'c8a6c923cf443ed6',
  twilioforshopify: 'd17e3d73885c6dd7d3d61a4ab7d8ad84',
  twilioformonday: '633bc2175f36f0587fff54b491d124cd',
  twilioforairtable: 'ee32ae39-6b7e-4eac-beb1-2a9f22dcd692',
};

export const TWILIOCALL_CLIENT_IDS: Record<string, string> = {
  twiliocallforhubspotcrm: '28e3fcba-92f6-4301-8a82-7381f8b658a9',
  twiliocallforbitrix24: 'app.618df869835df8.59780487',
  twiliocallforpipedrive: '7ac23d0ac0ff6950',
  twiliocallforshopify: 'd17e3d73885c6dd7d3d61a4ab7d8ad84',
  twiliocallformonday: '633bc2175f36f0587fff54b491d124cd',
};

// ── Voice / TwiML Types ─────────────────────────────────────────────

export interface TwilioTwiMLApp {
  sid: string;
  friendly_name: string;
  voice_url: string;
  voice_method: string;
  status_callback: string;
  status_callback_method: string;
}

export interface TwilioCallResource {
  sid: string;
  from: string;
  to: string;
  status: string;
  direction: string;
  duration: string;
  start_time: string;
  end_time: string;
  price: string | null;
  price_unit: string;
  parent_call_sid: string | null;
  // Runtime additions for parent/child linking
  childCall?: TwilioCallResource;
  childCalls?: TwilioCallResource[];
  parentCall?: TwilioCallResource;
}

export interface TwilioRecordingResource {
  sid: string;
  duration: string;
  media_url: string;
  /** Account-level recording fields (returned when listing all recordings) */
  call_sid?: string;
  date_created?: string;
  date_updated?: string;
  source?: string;
  channels?: number;
  status?: string;
  price?: string | null;
  price_unit?: string;
  uri?: string;
}

// ── Twilio API Base URLs ─────────────────────────────────────────────

export const TWILIO_API_BASE = 'https://api.twilio.com/2010-04-01';
export const TWILIO_MESSAGING_V1 = 'https://messaging.twilio.com/v1';
export const TWILIO_MESSAGING_V2 = 'https://messaging.twilio.com/v2';
export const TWILIO_CONTENT_API = 'https://content.twilio.com/v1';
export const TWILIO_VALIDATE_URL = 'https://us-central1-ulgebra-license.cloudfunctions.net/msbrequest';
export const TWILIO_EVENTS_API = 'https://events.twilio.com/v1';

// ── Twilio message events (Event Streams) ────────────────────────────
//
// The Event Streams subscription created in `apiCreateSubscription` streams
// these `com.twilio.messaging.*` event types to Ulgebra. All are shown as
// enable/disable toggles on the incoming settings page:
//   • the inbound-message event — a new incoming message from a customer.
//   • the outbound delivery statuses — the life-cycle of messages you send.
// The Event Streams sink toggle is the master switch (creates/removes the
// whole subscription); these per-event toggles pick what flows through it.

/** Twilio Event Streams event type name for an incoming (inbound) message. */
export const TWILIO_INBOUND_MESSAGE_EVENT = 'com.twilio.messaging.inbound-message.received';

export interface TwilioMessageStatusEvent {
  /** Twilio Event Streams event type name (the subscription `Types` value). */
  id: string;
  /** Short label shown in settings. */
  label: string;
  /** One-line explanation shown under the label. */
  description: string;
}

/**
 * The message events a user can enable/disable on the incoming settings page.
 * The inbound-message event is first (a customer messaging in), followed by
 * the outbound delivery statuses in natural message-lifecycle order.
 */
export const TWILIO_MESSAGE_EVENTS: TwilioMessageStatusEvent[] = [
  { id: TWILIO_INBOUND_MESSAGE_EVENT, label: 'Incoming message received', description: 'A new message from a customer (SMS or WhatsApp)' },
  { id: 'com.twilio.messaging.message.queued', label: 'Queued', description: 'Message accepted by Twilio and waiting to be sent' },
  { id: 'com.twilio.messaging.message.sent', label: 'Sent', description: 'Message handed off to the carrier' },
  { id: 'com.twilio.messaging.message.delivered', label: 'Delivered', description: 'Carrier confirmed delivery to the recipient' },
  { id: 'com.twilio.messaging.message.undelivered', label: 'Undelivered', description: 'Carrier could not deliver the message' },
  { id: 'com.twilio.messaging.message.failed', label: 'Failed', description: 'Message failed before or during sending' },
  { id: 'com.twilio.messaging.message.read', label: 'Read', description: 'Recipient read the message (WhatsApp only)' },
];
